import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getFileCategories,
  getSubFileCategories,
  getUrgencyLevels,
  type LookupItem,
} from '../../services/criminalCaseRequests';
import { listIntranetOfficerOptions, type IntranetOfficerOption } from '../../services/userAdministration';

export function formatAllocationOfficerLabel(officer: IntranetOfficerOption): string {
  return officer.division ? `${officer.displayName} (${officer.division})` : officer.displayName;
}

/** Stable id for intranet officer fallback lookups (no user id on list API). */
export function intranetOfficerOptionId(officer: IntranetOfficerOption): string {
  const division = officer.division.trim();
  return division ? `${officer.displayName}|${division}` : officer.displayName;
}

export function useReportFilterLookups(selectedFileCategoryNames: string[]) {
  const { user } = useAuth();
  const [fileCategoryOptions, setFileCategoryOptions] = useState<LookupItem[]>([]);
  const [subFileCategoryOptions, setSubFileCategoryOptions] = useState<LookupItem[]>([]);
  const [urgencyLevelOptions, setUrgencyLevelOptions] = useState<LookupItem[]>([]);
  const [allocationOfficerOptions, setAllocationOfficerOptions] = useState<IntranetOfficerOption[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [subLookupsLoading, setSubLookupsLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    setLookupsLoading(true);
    void Promise.all([getFileCategories(), getUrgencyLevels(), listIntranetOfficerOptions()]).then(
      ([cats, urgency, officers]) => {
        if (!alive) return;
        if (cats.success) setFileCategoryOptions(cats.data);
        if (urgency.success) setUrgencyLevelOptions(urgency.data);
        if (officers.success) setAllocationOfficerOptions(officers.data);
        setLookupsLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!selectedFileCategoryNames.length) {
      setSubFileCategoryOptions([]);
      setSubLookupsLoading(false);
      return;
    }

    const categoryIds = selectedFileCategoryNames
      .map((name) => fileCategoryOptions.find((c) => c.name === name)?.id)
      .filter((id): id is string => Boolean(id));

    if (!categoryIds.length) {
      setSubFileCategoryOptions([]);
      setSubLookupsLoading(false);
      return;
    }

    let alive = true;
    setSubLookupsLoading(true);
    void Promise.all(categoryIds.map((id) => getSubFileCategories(id))).then((results) => {
      if (!alive) return;
      const merged = new Map<string, LookupItem>();
      for (const res of results) {
        if (!res.success) continue;
        for (const item of res.data) merged.set(item.id, item);
      }
      setSubFileCategoryOptions(
        [...merged.values()].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setSubLookupsLoading(false);
    });

    return () => {
      alive = false;
    };
  }, [selectedFileCategoryNames, fileCategoryOptions]);

  const fileCategoryNames = useMemo(
    () => fileCategoryOptions.map((c) => c.name),
    [fileCategoryOptions],
  );

  const subFileCategoryNames = useMemo(
    () => subFileCategoryOptions.map((s) => s.name),
    [subFileCategoryOptions],
  );

  const urgencyLevelNames = useMemo(
    () => urgencyLevelOptions.map((u) => u.name),
    [urgencyLevelOptions],
  );

  const allocationOfficerLabels = useMemo(
    () => allocationOfficerOptions.map(formatAllocationOfficerLabel),
    [allocationOfficerOptions],
  );

  const mapFileCategoryNamesToIds = (names: string[]) =>
    names
      .map((name) => fileCategoryOptions.find((c) => c.name === name)?.id)
      .filter((id): id is string => Boolean(id));

  const mapSubFileCategoryNamesToIds = (names: string[]) =>
    names
      .map((name) => subFileCategoryOptions.find((s) => s.name === name)?.id)
      .filter((id): id is string => Boolean(id));

  return {
    fileCategoryNames,
    subFileCategoryNames,
    urgencyLevelNames,
    allocationOfficerLabels,
    mapFileCategoryNamesToIds,
    mapSubFileCategoryNamesToIds,
    subFileCategoryDisabled: selectedFileCategoryNames.length === 0,
    lookupsLoading,
    subLookupsLoading,
  };
}
